import { useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { deliveryMethodHelpers, ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { deliveryMethods } from '@/constants'
import AppContext from '@/context/AppContext'
import { updateAddressField } from '@/helpers'
import { getRefundMethodStepRoute, getSingleRefundMethodToApply } from '@/helpers/refundMethodStep/refundMethodStep'
import routes from '@/Router/routes.constants'
import services from '@/services/services'

import { getGiftRecipientAddress, getShippingAddress } from '../helpers'

const useExchangeAddressForm = () => {
  const navigate = useNavigate()
  const { context, setContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const initialized = useRef(false)
  const { fetchData: updateShippingAddress, loading } = useFetch(services.updateShippingAddress, {
    doInitialRequest: false,
  })
  const { getAddress } = deliveryMethodHelpers()

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    if (Object.keys(context.orderInfo.exchange_shipping_address).length > 0) return

    const getAddress = context.orderInfo.is_gift_return ? getGiftRecipientAddress : getShippingAddress

    setContext(prev => ({
      ...prev,
      orderInfo: {
        ...prev.orderInfo,
        exchange_shipping_address: getAddress(prev.orderInfo),
      },
    }))
  }, [context.orderInfo.exchange_shipping_address, context.orderInfo.is_gift_return, setContext])

  const updateExchangeAddress = e => {
    const { name, value } = e.target

    setContext(prev => ({
      ...prev,
      orderInfo: {
        ...prev.orderInfo,
        exchange_shipping_address: updateAddressField(prev.orderInfo.exchange_shipping_address, name, value),
      },
    }))
  }

  const handleContinue = async () => {
    try {
      await updateShippingAddress({
        orderId: context.orderInfo.id,
        returnId: context.orderInfo.returnId,
        payload: {
          address: getAddress({
            ...context.orderInfo,
            shipping_address: context.orderInfo.exchange_shipping_address,
            deliveryMethod: { ...context.orderInfo.deliveryMethod, type: deliveryMethods.customerPickup }, // Force type to ensure full address validation
          }),
        },
      })

      const fromState = { state: { from: { pathname: routes.exchangeAddressPage } } }
      const allowedRefundMethods = context.orderInfo.allowed_refund_methods
      const refundMethodToApply = getSingleRefundMethodToApply({ context, allowedRefundMethods })

      navigate(getRefundMethodStepRoute({ allowedRefundMethods, refundMethodToApply }), fromState)
    } catch (error) {
      setToastInfo({ show: true, message: error?.errorMessage, type: 'error' })
    }
  }

  return {
    updateExchangeAddress,
    handleContinue,
    exchangeShippingAddress: context?.orderInfo?.exchange_shipping_address,
    provinces: context?.allowed_region?.provinces?.map(p => ({ value: p.code, label: p.name })) || [],
    loading,
  }
}

export default useExchangeAddressForm
